import { PlusOutlined, UploadOutlined } from '@ant-design/icons'
import useCarers from '@features/organisation/hooks/useCarers'
import type { Home, ResidentItem } from '@lmc/ts-common'
import {
  Avatar,
  Button,
  DatePicker,
  Form,
  FormItem,
  Heading,
  Input,
  TextArea
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import {
  Form as AntdForm,
  Divider,
  Modal,
  Select,
  Upload,
  type UploadFile
} from 'antd'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { residentHealthRecordsConstants } from 'locale/residentHealthRecords'
import { lowerFirst } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { MAX_DOCUMENTS } from '../constants'
import { useCommunicationRecordCreate } from '../hooks/useCommunicationRecordCreate'
import { useCommunicationRecordUpdate } from '../hooks/useCommunicationRecordUpdate'
import {
  CommunicationStatus,
  getStatusDescription,
  getStatusLabel
} from '../utils/communicationRecordUtils'
import type {
  CommunicationRecord,
  CommunicationStatus as CommunicationStatusType
} from '../utils/types'

export type CommunicationRecordModalProps = {
  open: boolean
  onClose: () => void
  resident?: ResidentItem
  initialRecord?: CommunicationRecord | null
}

const CommunicationRecordModal = ({
  open,
  onClose,
  resident,
  initialRecord
}: CommunicationRecordModalProps) => {
  const { t } = useTranslation(NAMESPACE.RESIDENT_HEALTH_RECORDS)

  const currentHome: Home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const currentHomeId: string = currentHome.id

  const residentId = resident?.id || ''

  const [form] = AntdForm.useForm()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const selectedStatus = AntdForm.useWatch('communicationStatus', form)
  const isEditMode = !!initialRecord
  const existingDocumentsCount = initialRecord?.documents?.length || 0
  const remainingSlots = MAX_DOCUMENTS - existingDocumentsCount

  const { carers } = useCarers()

  const { createCommunicationRecord, isCreateLoading } =
    useCommunicationRecordCreate({
      onSuccess: () => {
        onClose()
      }
    })

  const { updateCommunicationRecord, isUpdateLoading } =
    useCommunicationRecordUpdate({
      onSuccess: () => {
        onClose()
      }
    })

  const filteredCarers = carers?.filter(
    (carerPerson) =>
      carerPerson.name.first !== undefined &&
      carerPerson.name.last !== undefined &&
      carerPerson.active &&
      (carerPerson.home === currentHomeId ||
        carerPerson.homes.includes(currentHomeId))
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (open) {
      if (initialRecord) {
        const formValues = {
          title: initialRecord.title || '',
          description: initialRecord.description || '',
          sentBy: initialRecord.sentBy || undefined,
          recipient: initialRecord.recipient || undefined,
          communicationStatus: initialRecord.communicationStatus || undefined,
          dateSent: initialRecord.dateSent
            ? dayjs(initialRecord.dateSent)
            : undefined,
          resident: residentId
        }
        form.setFieldsValue(formValues)
        // File uploads can't be prefilled, so fileList stays empty
        setFileList([])
      } else {
        form.resetFields()
        setFileList([])
        form.setFieldsValue({ resident: residentId })
      }
    }
  }, [open, resident, form, initialRecord])

  const handleFileChange = (info: { fileList: UploadFile[] }) => {
    // Limit based on remaining slots (MAX_DOCUMENTS - existing documents)
    const maxAllowed = isEditMode ? remainingSlots : MAX_DOCUMENTS
    const limitedFileList = info.fileList.slice(-maxAllowed)
    setFileList(limitedFileList)
  }

  const handleSubmit = async (values: unknown) => {
    const formValues = values as {
      title?: string
      name?: string
      description?: string
      category?: string
      subCategory?: string
      resident: string
      communicationStatus?: CommunicationStatusType
      sentBy?: string
      recipient?: string
      dateSent?: string
    }

    const submitValues = {
      ...formValues,
      name: formValues.title || formValues.name,
      resident: residentId
    }

    let cleanedSubmitValues: typeof submitValues
    if (initialRecord) {
      cleanedSubmitValues = {
        ...submitValues,
        sentBy: submitValues.sentBy === undefined ? null : submitValues.sentBy,
        recipient:
          submitValues.recipient === undefined ? null : submitValues.recipient,
        dateSent:
          submitValues.dateSent === undefined ? null : submitValues.dateSent
      } as typeof submitValues
    } else {
      cleanedSubmitValues = Object.fromEntries(
        Object.entries(submitValues).filter(([_, value]) => value !== undefined)
      ) as typeof submitValues
    }

    if (initialRecord) {
      const recordId = initialRecord.id || (initialRecord as any)._id

      // Extract existing document IDs to merge with new uploads
      const existingDocumentIds = initialRecord.documents
        ? initialRecord.documents.map((doc: any) => doc._id || doc.id)
        : []

      await updateCommunicationRecord(
        { _id: recordId, ...cleanedSubmitValues, existingDocumentIds },
        fileList
      )
    } else {
      await createCommunicationRecord(cleanedSubmitValues, fileList)
    }
  }

  if (!open) return null

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={
        <ModalFooter>
          <Button onClick={onClose}>
            {t(residentHealthRecordsConstants.CANCEL)}
          </Button>
          <Button
            onClick={form.submit}
            variant="primary"
            isLoading={initialRecord ? isUpdateLoading : isCreateLoading}
          >
            {initialRecord ? (
              t(residentHealthRecordsConstants.SAVE)
            ) : (
              <>
                <PlusOutlined />
                {t(residentHealthRecordsConstants.ADD)}
              </>
            )}
          </Button>
        </ModalFooter>
      }
      width={600}
    >
      <Heading level={3}>
        {t(residentHealthRecordsConstants.COMMUNICATION_RECORD)}
      </Heading>
      <Divider />
      <StyledForm form={form} onFinish={handleSubmit} layout="horizontal">
        <FormItem
          name="title"
          label={
            <Text>{t(residentHealthRecordsConstants.COMMUNICATION_TITLE)}</Text>
          }
          rules={[
            {
              required: true,
              message: `${t(residentHealthRecordsConstants.COMMUNICATION_TITLE)} ${t(residentHealthRecordsConstants.IS_REQUIRED)}`
            }
          ]}
        >
          <Input
            placeholder={`${t(residentHealthRecordsConstants.ENTER)} ${lowerFirst(t(residentHealthRecordsConstants.COMMUNICATION_TITLE))}`}
          />
        </FormItem>

        <StyledFormItemWithTopAsterisk
          name="description"
          label={
            <Text>
              {t(residentHealthRecordsConstants.COMMUNICATION_DESCRIPTION) +
                ':'}
            </Text>
          }
          rules={[
            {
              required: true,
              message: `${t(residentHealthRecordsConstants.COMMUNICATION_DESCRIPTION)} ${t(residentHealthRecordsConstants.IS_REQUIRED)}`
            }
          ]}
          colon={false}
        >
          <TextArea
            rows={4}
            placeholder={`${t(residentHealthRecordsConstants.ENTER)} ${lowerFirst(t(residentHealthRecordsConstants.COMMUNICATION_DESCRIPTION))}`}
          />
        </StyledFormItemWithTopAsterisk>

        <FormItem
          name="document"
          label={
            <Text>{t(residentHealthRecordsConstants.DOCUMENT_UPLOAD)}</Text>
          }
        >
          {isEditMode && existingDocumentsCount > 0 && (
            <InfoText>
              {existingDocumentsCount} existing document
              {existingDocumentsCount > 1 ? 's' : ''}. You can add{' '}
              {remainingSlots} more.
            </InfoText>
          )}
          <Upload
            fileList={fileList}
            beforeUpload={() => false}
            onChange={handleFileChange}
            accept=".pdf,.png,.jpg,.jpeg,.heic,.docm,.gif,.docx,.doc,.xlsx,.xls,.txt,.csv,.ppt,.pptx"
            maxCount={isEditMode ? remainingSlots : MAX_DOCUMENTS}
          >
            <Button
              disabled={
                isEditMode
                  ? fileList.length >= remainingSlots
                  : fileList.length >= MAX_DOCUMENTS
              }
            >
              <UploadOutlined />
              {t(residentHealthRecordsConstants.UPLOAD_DOCUMENT)}
              {isEditMode
                ? fileList.length > 0 &&
                  ` (${fileList.length}/${remainingSlots})`
                : fileList.length > 0 &&
                  ` (${fileList.length}/${MAX_DOCUMENTS})`}
            </Button>
          </Upload>
          {((isEditMode && fileList.length >= remainingSlots) ||
            (!isEditMode && fileList.length >= MAX_DOCUMENTS)) && (
            <ErrorText>
              {isEditMode
                ? `Maximum ${remainingSlots} additional document${remainingSlots !== 1 ? 's' : ''} allowed`
                : 'Maximum 3 documents allowed'}
            </ErrorText>
          )}
        </FormItem>

        <FormItem
          name="sentBy"
          label={<Text>{t(residentHealthRecordsConstants.WHO_SENT)}</Text>}
        >
          <Select
            allowClear
            placeholder={
              <Text>{`${t(residentHealthRecordsConstants.SELECT)} ${lowerFirst(t(residentHealthRecordsConstants.WHO_SENT))}`}</Text>
            }
          >
            {filteredCarers?.map((carer) => (
              <Select.Option
                key={carer.id}
                value={carer.id}
                label={`${carer.name.first} ${carer.name.last}`}
              >
                <OptionContainer>
                  <Avatar url={carer.picture as unknown as string} size={20} />
                  <Text>
                    {carer.name.first} {carer.name.last}
                  </Text>
                </OptionContainer>
              </Select.Option>
            ))}
          </Select>
        </FormItem>

        <FormItem
          name="recipient"
          label={<Text>{t(residentHealthRecordsConstants.RECIPIENT)}</Text>}
        >
          <Select
            allowClear
            placeholder={
              <Text>{`${t(residentHealthRecordsConstants.SELECT)} ${lowerFirst(t(residentHealthRecordsConstants.RECIPIENT))}`}</Text>
            }
          >
            {resident?.importantContacts?.map((contact) => {
              const contactId = (contact as unknown as { id: string }).id
              return (
                <Select.Option
                  key={contactId}
                  value={contactId}
                  label={`${contact.name.first} ${contact.name.last}`}
                >
                  <Text>
                    {contact.name.first} {contact.name.last}
                  </Text>
                </Select.Option>
              )
            })}
          </Select>
        </FormItem>

        <StyledFormItemWithTopAsterisk
          name="communicationStatus"
          label={
            <Text>
              {t(residentHealthRecordsConstants.STATUS_OF_COMMUNICATION) + ':'}
            </Text>
          }
          rules={[
            {
              required: true,
              message: `${t(residentHealthRecordsConstants.STATUS_OF_COMMUNICATION)} ${t(residentHealthRecordsConstants.IS_REQUIRED)}`
            }
          ]}
          colon={false}
          extra={
            selectedStatus ? (
              <StatusDescriptionText level={1.5}>
                {t(getStatusDescription(selectedStatus))}
              </StatusDescriptionText>
            ) : undefined
          }
        >
          <Select
            placeholder={
              <Text>{`${t(residentHealthRecordsConstants.SELECT)} ${lowerFirst(t(residentHealthRecordsConstants.STATUS_OF_COMMUNICATION))}`}</Text>
            }
          >
            {Object.values(CommunicationStatus).map((status) => (
              <Select.Option key={status} value={status}>
                <Text>{t(getStatusLabel(status))}</Text>
              </Select.Option>
            ))}
          </Select>
        </StyledFormItemWithTopAsterisk>

        <FormItem
          name="dateSent"
          label={<Text>{t(residentHealthRecordsConstants.DATE_SENT)}</Text>}
        >
          <DatePicker />
        </FormItem>
        <Divider />
      </StyledForm>
    </Modal>
  )
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledFormItemWithTopAsterisk = styled(FormItem)`
  .ant-form-item-label > label {
    display: flex;
    align-items: flex-start;
  }

  .ant-form-item-required::before {
    margin-top: 8px;
  }
`

const StatusDescriptionText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const ErrorText = styled(Text)`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
  display: block;
`

const InfoText = styled(Text)`
  font-size: 12px;
  margin-bottom: 8px;
  display: block;
`

export default CommunicationRecordModal
